import React, { useState, useEffect, useCallback } from 'react';
import {
  X,
  Settings,
  Shield,
  BarChart3,
  Zap,
  Globe,
} from 'lucide-react';
import {
  saveCookieConsent,
  needsCookieConsent,
  getEffectivePreferences,
  COOKIE_CONSENT_UPDATED_EVENT,
  type CookiePreferences,
} from 'src/config/consent/cookieConsentStore';

const CookieBanner: React.FC = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    essential: true,
    analytics: false,
    functional: false,
    marketing: false,
  });

  // needsCookieConsent() covers: never asked, version bumped, or over a year old.
  const syncFromStore = useCallback(() => {
    setPreferences(getEffectivePreferences());
    setShowBanner(needsCookieConsent());
  }, []);

  useEffect(() => {
    if (!needsCookieConsent()) {
      setPreferences(getEffectivePreferences());
      return;
    }
    const timer = setTimeout(() => setShowBanner(true), 500);
    return () => clearTimeout(timer);
  }, []);

  // The account's choice can arrive after sign-in and supersede the local one.
  useEffect(() => {
    window.addEventListener(COOKIE_CONSENT_UPDATED_EVENT, syncFromStore);
    return () => window.removeEventListener(COOKIE_CONSENT_UPDATED_EVENT, syncFromStore);
  }, [syncFromStore]);

  // Allow external components to open cookie settings via a custom event
  useEffect(() => {
    const handleOpenSettings = () => setShowSettings(true);
    window.addEventListener('openCookieSettings', handleOpenSettings);
    return () => window.removeEventListener('openCookieSettings', handleOpenSettings);
  }, []);

  const handleAcceptAll = () => {
    const allAccepted: CookiePreferences = { essential: true, analytics: true, functional: true, marketing: true };
    setPreferences(allAccepted);
    saveCookiePreferences(allAccepted);
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleRejectNonessential = () => {
    const essentialOnly: CookiePreferences = { essential: true, analytics: false, functional: false, marketing: false };
    setPreferences(essentialOnly);
    saveCookiePreferences(essentialOnly);
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleSaveSettings = () => {
    saveCookiePreferences(preferences);
    setShowSettings(false);
    setShowBanner(false);
  };

  // Via the store, so the choice is versioned, sent to the API and purged for.
  const saveCookiePreferences = (prefs: CookiePreferences) => {
    saveCookieConsent(prefs);
  };

  const togglePreference = (key: keyof CookiePreferences) => {
    if (key === 'essential') return;
    setPreferences((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // === BANNER ===
  const renderBanner = () => {
    if (!showBanner || showSettings) return null;
    return (
      <div className="fixed bottom-0 left-0 right-0 z-[9999] flex max-h-[60vh] sm:max-h-[85vh] flex-col bg-white dark:bg-surface-dark-50 shadow-[0_-4px_24px_rgba(0,0,0,0.12)] animate-slide-up">
        {/* Dark Header  */}
        <div className="flex-shrink-0 bg-navy-500 px-4 sm:px-6 py-3 flex items-center gap-2.5">
          <Shield size={20} className="text-white" />
          <span className="text-white font-semibold text-body-sm">We value your privacy</span>
        </div>

        <div className="flex min-h-0 flex-1 flex-col px-4 sm:px-6 py-4">
          <div className="flex min-h-0 flex-1 flex-col md:flex-row gap-4 md:gap-6 md:items-start">
            <div className="min-h-0 flex-1 overflow-y-auto">
              <p className="text-body-sm text-navy-500 dark:text-slate-300 leading-relaxed mb-3">
                We use cookies and similar technologies to make this site work, to understand how it is used, and to improve our services. Some cookies are essential, while others are used for analytics and optional features.
                <br /><br />
                By clicking "Accept all", you agree to the use of nonessential cookies. You can change your preferences at any time in Cookie settings. For more details, please read our{' '}
                <a href="/privacy-policy" className="text-brand-500 font-semibold underline hover:text-brand-700">
                  Privacy Policy
                </a>{' '}and Cookie Policy.
              </p>

              <div className="bg-surface-50 dark:bg-surface-dark-200 px-3 py-2.5 rounded-lg">
                <p className="text-body-sm text-slate-500 dark:text-slate-400 leading-relaxed">
                  <strong>Note:</strong> Your medical records and health information are processed only when you request a second opinion and are handled under our Patient Agreement and Informed Consent and Privacy Policy, not through cookies.
                </p>
              </div>
            </div>

            {/* Buttons */}
            <div className="grid flex-shrink-0 grid-cols-1 gap-2 sm:flex sm:flex-row sm:flex-wrap md:flex-col md:flex-nowrap justify-end">
              <button
                onClick={handleAcceptAll}
                className="px-4 sm:px-6 py-2 bg-brand-500 text-white font-semibold text-body-sm rounded-lg shadow-[0_2px_8px_rgba(58,134,255,0.25)] hover:bg-brand-600 hover:shadow-[0_4px_12px_rgba(58,134,255,0.35)] transition-all whitespace-nowrap min-w-[100px]"
              >
                Accept all
              </button>
              <button
                onClick={handleRejectNonessential}
                className="px-3 sm:px-5 py-2 border border-slate-300 dark:border-surface-dark-200 text-navy-500 dark:text-slate-300 font-semibold text-body-sm rounded-lg hover:border-slate-400 hover:bg-surface-50 dark:hover:bg-surface-dark-200 transition-all whitespace-nowrap min-w-[130px]"
              >
                Reject Non-essential
              </button>
              <button
                onClick={() => setShowSettings(true)}
                className="px-3 sm:px-5 py-2 border border-brand-500 text-brand-500 font-semibold text-body-sm rounded-lg hover:bg-brand-50 dark:hover:bg-brand-500/10 transition-all whitespace-nowrap min-w-[115px]"
              >
                Cookie settings
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // === SETTINGS DIALOG ===
  const renderSettings = () => {
    if (!showSettings) return null;
    return (
      <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/45 backdrop-blur-sm p-4">
        <div className="w-full max-w-3xl max-h-[90vh] bg-white dark:bg-surface-dark-50 rounded-3xl overflow-hidden shadow-[0_8px_32px_rgba(58,134,255,0.15)] flex flex-col">
          {/* Header */}
          <div className="bg-gradient-to-br from-brand-500 to-brand-700 px-5 py-4 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="bg-white/20 backdrop-blur-md p-1.5 rounded-xl flex">
                <Settings size={22} className="text-white" />
              </div>
              <h2 className="text-white font-semibold text-lg">Cookie Settings</h2>
            </div>
            <button
              onClick={() => setShowSettings(false)}
              className="text-white/80 hover:text-white hover:bg-white/10 rounded-lg p-1.5 transition-colors"
            >
              <X size={18} />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-5 bg-surface-50 dark:bg-surface-dark">
            <p className="text-body-sm text-navy-500 dark:text-slate-300 leading-relaxed mb-5">
              You can choose which types of cookies and similar technologies we use on this site.
              You can change your choices at any time. Essential cookies are always on because they
              are needed for the site to function.
            </p>

            <div className="space-y-3">
              {/* Essential */}
              <div className="bg-white dark:bg-surface-dark-50 border-2 border-brand-100 dark:border-brand-500/20 rounded-2xl p-4 shadow-soft-sm">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2.5">
                    <div className="bg-brand-500 p-1.5 rounded-xl flex">
                      <Shield size={18} className="text-white" />
                    </div>
                    <div>
                      <span className="text-body font-bold text-navy-500 dark:text-slate-100">
                        Strictly necessary cookies
                      </span>
                      <span className="ml-2 inline-block px-2 py-0.5 bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 text-ui-sm font-semibold rounded">
                        always active
                      </span>
                    </div>
                  </div>
                </div>
                <p className="text-body-sm text-slate-500 dark:text-slate-400 leading-relaxed ml-10">
                  These cookies are essential for the website to work properly and cannot be switched off.
                </p>
              </div>

              {renderCookieCategory('analytics', 'Analytics and performance cookies',
                'These cookies help us understand how visitors use our site so we can measure and improve performance.',
                <BarChart3 size={18} className="text-white" />
              )}

              {renderCookieCategory('functional', 'Functional cookies',
                'These cookies enable enhanced functionality and personalisation, such as remembering your preferences.',
                <Zap size={18} className="text-white" />
              )}

              {renderCookieCategory('marketing', 'Marketing / communication cookies',
                'These cookies may be used to show you relevant information about our services on this or other websites.',
                <Globe size={18} className="text-white" />
              )}
            </div>

            {/* Disclaimer */}
            <div className="mt-5 bg-surface-100 dark:bg-surface-dark-200 px-4 py-3 rounded-lg border-l-[3px] border-brand-500">
              <p className="text-body-sm text-slate-500 dark:text-slate-400 leading-relaxed">
                <strong>Note:</strong> Your medical records and health information are processed only when you request a second opinion and are handled under our Patient Agreement and Informed Consent and{' '}
                <a href="/privacy-policy" className="text-brand-500 font-semibold underline">Privacy Policy</a>, not through cookies.
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-brand-100 dark:border-surface-dark-200 p-5 bg-brand-50/50 dark:bg-surface-dark flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleSaveSettings}
              className="flex-1 sm:flex-none sm:min-w-[150px] px-6 py-2.5 bg-brand-500 text-white font-semibold rounded-xl shadow-[0_2px_8px_rgba(58,134,255,0.25)] hover:bg-brand-600 transition-all"
            >
              Save settings
            </button>
            <button
              onClick={handleAcceptAll}
              className="flex-1 sm:flex-none sm:min-w-[150px] px-6 py-2.5 border-2 border-brand-500 text-brand-500 font-semibold rounded-xl hover:bg-brand-50 dark:hover:bg-brand-500/10 transition-all"
            >
              Accept all
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderCookieCategory = (
    key: keyof CookiePreferences,
    title: string,
    description: string,
    icon: React.ReactNode
  ) => {
    const isActive = preferences[key];

    return (
      <div
        className={[
          'bg-white dark:bg-surface-dark-50 border-2 rounded-2xl p-4 transition-all duration-300',
          isActive
            ? 'border-brand-500 shadow-[0_2px_12px_rgba(58,134,255,0.15)]'
            : 'border-brand-100 dark:border-surface-dark-200 shadow-soft-sm',
        ].join(' ')}
      >
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-2.5 flex-1">
            <div className={`${isActive ? 'bg-brand-500' : 'bg-slate-400'} p-1.5 rounded-xl flex transition-colors duration-300`}>
              {icon}
            </div>
            <span className="text-body font-bold text-navy-500 dark:text-slate-100">{title}</span>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={isActive}
              onChange={() => togglePreference(key)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-slate-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-brand-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand-500" />
          </label>
        </div>
        <p className="text-body-sm text-slate-500 dark:text-slate-400 leading-relaxed ml-10">
          {description}
        </p>
      </div>
    );
  };

  return (
    <>
      {renderBanner()}
      {renderSettings()}
    </>
  );
};

export default CookieBanner;
